import '@/App.css';
import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { CartProvider } from '@/context/CartContext';
import ScrollToTop from '@/components/ScrollToTop';
import SmoothScroll from '@/components/motion/SmoothScroll';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CartDrawer from '@/components/CartDrawer';

import Landing from '@/pages/Landing';
import Shop from '@/pages/Shop';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import OrderSuccess from '@/pages/OrderSuccess';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import Account from '@/pages/Account';
import AdminDashboard from '@/pages/admin/AdminDashboard';

// Fast, subtle page enter/exit (reduced-motion => simple fade).
const Page = ({ children }) => {
    const reduce = useReducedMotion();
    return (
        <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.div>
    );
};

function AnimatedApp() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith('/admin');

    if (isAdmin) {
        return (
            <Routes location={location}>
                <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
        );
    }

    return (
        <>
            <Navbar />
            <main id="main-content" className="min-h-screen">
                <AnimatePresence mode="wait" initial={false}>
                    <Routes location={location} key={location.pathname}>
                        <Route path="/" element={<Page><Landing /></Page>} />
                        <Route path="/shop" element={<Page><Shop /></Page>} />
                        <Route path="/product/:slug" element={<Page><ProductDetail /></Page>} />
                        <Route path="/cart" element={<Page><Cart /></Page>} />
                        <Route path="/checkout" element={<Page><Checkout /></Page>} />
                        <Route path="/order/:orderId" element={<Page><OrderSuccess /></Page>} />
                        <Route path="/login" element={<Page><Login /></Page>} />
                        <Route path="/signup" element={<Page><Signup /></Page>} />
                        <Route path="/account" element={<Page><Account /></Page>} />
                    </Routes>
                </AnimatePresence>
            </main>
            <Footer />
            <CartDrawer />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <CartProvider>
                        <SmoothScroll />
                        <ScrollToTop />
                        <a href="#main-content" className="skip-link">Skip to content</a>
                        <AnimatedApp />
                        <Toaster richColors position="top-center" />
                    </CartProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
